// Import necessary libraries
import React from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
} from "chart.js";

// Register required components
ChartJS.register(
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend
);

const STATUS_SERIES = [
  {
    key: "compliant",
    label: "Compliant",
    color: "#3DB77E",
    borderColor: "#86E5B2",
    backgroundColor: "rgba(134, 229, 178, 0.3)",
  },
  {
    key: "nonCompliant",
    label: "Non-Compliant",
    color: "#F04438",
    borderColor: "#F04438",
    backgroundColor: "#F04438",
  },
  {
    key: "invited",
    label: "Invited",
    color: "#0E4E9A",
    borderColor: "#0E4E9A",
    backgroundColor: "rgba(152, 162, 179, 0.3)",
  },
  {
    key: "registered",
    label: "Self-Registered",
    color: "#84BFF3",
    borderColor: "#84BFF3",
    backgroundColor: "rgba(132, 191, 243, 0.3)",
  },
  {
    key: "assigned",
    label: "Registered",
    color: "#5786CC",
    borderColor: "#5786CC",
    backgroundColor: "rgba(87, 134, 204, 0.3)",
  },
  {
    key: "closed",
    label: "Deactivated",
    color: "#FDB022",
    borderColor: "#FDB022",
    backgroundColor: "rgba(253, 176, 34, 0.3)",
  },
];

const getStatusValue = (entry, key) => {
  if (key === "nonCompliant") {
    return Number(entry?.nonCompliant || entry?.non_compliant) || 0;
  }
  return Number(entry?.[key]) || 0;
};

const LineChart = ({ chartData, count, activeCount }) => {
  const safeChartData = Array.isArray(chartData) ? chartData : [];
  const latestEntry =
    safeChartData.length > 0
      ? safeChartData[safeChartData.length - 1]
      : {};
  const safeActiveCount = activeCount ?? latestEntry;

  const monthLabels = safeChartData.map((entry) => {
    const date = new Date(entry.month + "-01"); // Ensure it's parsed correctly
    return date.toLocaleString("default", { month: "short" }); // e.g., "Mar"
  });

  // Extract `compliant` and `nonCompliant` values from `chartData`
  // const compliantData = safeChartData.map((entry) => entry.compliant);
  // const nonCompliantData = safeChartData.map((entry) => entry.nonCompliant);
  // const invitedData = safeChartData.map((entry) => entry.invited);
  // const registeredData = safeChartData.map((entry) => entry.registered);
  // const closedData = safeChartData.map((entry) => entry.closed);
  const compliantData = safeChartData.map((e) => Number(e?.compliant ?? 0));
  const nonCompliantData = safeChartData.map((e) => Number(e?.nonCompliant ?? 0));
  const invitedData = safeChartData.map((e) => Number(e?.invited ?? 0));
  const registeredData = safeChartData.map((e) => Number(e?.registered ?? 0));
  const closedData = safeChartData.map((e) => Number(e?.closed ?? 0));
  const assignedData = safeChartData.map((e) => Number(e?.assigned ?? 0));
  // Define data for chart using shared STATUS_SERIES
  const datasets = STATUS_SERIES.map((series) => ({
    label: series.label,
    data: safeChartData.map((e) => getStatusValue(e, series.key)),
    borderColor: series.borderColor,
    backgroundColor: series.backgroundColor,
    pointBackgroundColor: series.color,
    pointBorderColor: "#fff",
    pointBorderWidth: 2,
    pointRadius: 5,
    pointHoverRadius: 7,
    fill: true,
    lineTension: 0.4,
    borderWidth: 2,
    fill: false,
    yAxisID: "y"
  }));

  const data = {
    // labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    labels: monthLabels,
    datasets,
  };

  // Define chart options
  const options = {
    responsive: true,
    maintainAspectRatio: false, // Disable aspect ratio to allow custom sizing
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        callbacks: {
          label: (tooltipItem) =>
            `${tooltipItem.dataset.label}: ${tooltipItem.raw}`,
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 12 } },
      },
      y: {
        beginAtZero: true,
        ticks: { font: { size: 12 } },
      },

    }
  };

  return (
    <div
      style={{
        width: "100%",
        height: "400px", // Set height of the chart container
        margin: "0 auto",
        paddingTop: "60px",
        position: "relative",
      }}
    >
      {/* Title on the left side */}
      <div style={{ position: "absolute", top: "0", left: "0" }}>
        <span className="text-gray-600 font-medium text-xs">
          Total Providers
        </span>
        <div className="text-gray-600 font-semibold text-sm">{count}</div>
      </div>

      {/* Custom Legend on the right side */}
      <div
        style={{
          position: "absolute",
          top: "0",
          right: "0",
          display: "flex",
          gap: "20px",
          alignItems: "center",
        }}
      >
        {STATUS_SERIES.map((series) => (
          <div key={series.key} className="flex gap-2">
            <div
              className="mt-2"
              style={{
                width: "8px",
                height: "8px",
                backgroundColor: series.color,
                borderRadius: "50%",
              }}
            ></div>
            <div>
              <span className="text-gray-600 font-medium text-xs">{series.label}</span>
              <div className="text-gray-600 font-semibold text-sm">
                {getStatusValue(safeActiveCount, series.key)}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Chart */}
      <div style={{ width: "100%", height: "100%" }}>
        <Line data={data} options={options} />
      </div>
    </div>
  );
};

export default LineChart;
